import React, { useState, useRef } from 'react';
import './ImageUpload.css';
import cloudinaryService, { UPLOAD_FOLDERS } from '../../../services/cloudinary/cloudinary.service';

const ImageUpload = ({ 
  onUploadComplete, 
  currentImage = null,
  folder = UPLOAD_FOLDERS.PRODUCTS,
  maxSize = 5,
  multiple = false,
  buttonText = 'Télécharger une image'
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setError(null);
    setUploading(true);
    setProgress(0);

    try {
      if (multiple) {
        // Multiple images upload
        const uploadedImages = [];
        const errors = [];
        
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          
          // Validate file
          const validation = cloudinaryService.validateImage(file, {
            maxSize: maxSize * 1024 * 1024,
            allowedTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/jpg']
          });

          if (!validation.valid) {
            errors.push(`${file.name}: ${validation.error}`);
            continue;
          }

          // Upload to Cloudinary
          const result = await cloudinaryService.uploadImage(file, {
            folder: folder,
            tags: ['product']
          });

          if (result.success) {
            uploadedImages.push({
              url: result.url,
              publicId: result.publicId,
              width: result.width,
              height: result.height
            });
          } else {
            errors.push(`${file.name}: ${result.error}`);
          }

          // Update progress
          setProgress(Math.round(((i + 1) / files.length) * 100));
        }

        if (errors.length > 0) {
          setError(errors.join(', '));
        }

        if (uploadedImages.length > 0) {
          // For multiple images, pass array to parent
          onUploadComplete(uploadedImages);
        }
      } else {
        // Single image upload
        const file = files[0];
        
        // Validate file
        const validation = cloudinaryService.validateImage(file, {
          maxSize: maxSize * 1024 * 1024,
          allowedTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/jpg']
        });

        if (!validation.valid) {
          setError(validation.error);
          setUploading(false);
          return;
        }

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => setPreview(e.target.result);
        reader.readAsDataURL(file);

        // Upload to Cloudinary
        const result = await cloudinaryService.uploadImage(file, {
          folder: folder,
          tags: ['product']
        });

        if (result.success) {
          setPreview(result.url);
          onUploadComplete({
            url: result.url,
            publicId: result.publicId,
            width: result.width,
            height: result.height
          });
        } else {
          setError(result.error);
          setPreview(null);
        }
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Une erreur est survenue lors du téléchargement');
    } finally {
      setUploading(false);
      if (!multiple) {
        setProgress(0);
      }
      // Clear file input for next upload
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onUploadComplete(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // For multiple mode, don't show preview - parent handles display
  if (multiple) {
    return (
      <div className="image-upload">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple={true}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          disabled={uploading}
        />

        <div 
          className={`upload-zone ${uploading ? 'uploading' : ''}`}
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          {uploading ? (
            <div className="upload-progress">
              <div className="spinner"></div>
              <p>Téléchargement... {progress}%</p>
              {progress > 0 && (
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                </div>
              )}
            </div>
          ) : (
            <>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              <p className="upload-text">{buttonText}</p>
              <p className="upload-hint">PNG, JPG, WEBP (Max {maxSize}MB) - Sélectionnez plusieurs images</p>
            </>
          )}
        </div>

        {error && (
          <div className="upload-error">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            {error}
          </div>
        )}
      </div>
    );
  }

  // Single image mode with preview
  return (
    <div className="image-upload">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        disabled={uploading}
      />

      {!preview ? (
        <div 
          className={`upload-zone ${uploading ? 'uploading' : ''}`}
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          {uploading ? (
            <div className="upload-progress">
              <div className="spinner"></div>
              <p>Téléchargement... {progress}%</p>
              {progress > 0 && (
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                </div>
              )}
            </div>
          ) : (
            <>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              <p className="upload-text">{buttonText}</p>
              <p className="upload-hint">PNG, JPG, WEBP (Max {maxSize}MB)</p>
            </>
          )}
        </div>
      ) : (
        <div className="image-preview">
          <img src={preview} alt="Preview" />
          <div className="preview-overlay">
            <button
              type="button"
              className="btn-remove"
              onClick={handleRemove}
              disabled={uploading}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              Supprimer
            </button>
            <button
              type="button"
              className="btn-change"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Changer
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="upload-error">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {error}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;